import { Link } from "react-router-dom";
import { Check, ArrowRight, Lightbulb, Users, GitCompare } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

function Section({ title, children, id }) {
  return (
    <section id={id} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight mb-5">{title}</h2>
      {children}
    </section>
  );
}

export function ToolContent({ cfg }) {
  return (
    <div className="bg-white border-t border-slate-200">
      <Section title={`Why the ${cfg.name}?`}>
        <div className="space-y-4 text-base text-slate-600 leading-relaxed">
          {cfg.why.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </Section>

      <Section title="Key features">
        <ul className="grid sm:grid-cols-2 gap-3">
          {cfg.features.map((f, i) => {
            const [lead, ...rest] = f.split(":");
            return (
              <li key={i} className="flex items-start gap-3 bg-slate-50 rounded-xl p-4 border border-slate-100">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3.5 h-3.5" />
                </span>
                <span className="text-sm text-slate-700">
                  {rest.length ? <><strong className="text-slate-900">{lead}:</strong>{rest.join(":")}</> : f}
                </span>
              </li>
            );
          })}
        </ul>
      </Section>

      <Section title="How it works">
        <ol className="space-y-4">
          {cfg.steps.map((s, i) => (
            <li key={i} className="flex items-start gap-4">
              <span className="w-8 h-8 rounded-lg brand-gradient text-white font-bold flex items-center justify-center shrink-0 text-sm">{i + 1}</span>
              <p className="text-base text-slate-600 pt-1">{s}</p>
            </li>
          ))}
        </ol>
      </Section>

      <div className="bg-slate-50 border-y border-slate-200">
        <Section title="Benefits">
          <ul className="grid sm:grid-cols-2 gap-3">
            {cfg.benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-3">
                <ArrowRight className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">{b}</span>
              </li>
            ))}
          </ul>
        </Section>
      </div>

      <Section title="Who it's for">
        <div className="flex flex-wrap gap-3">
          {cfg.whoFor.map((w, i) => (
            <span key={i} className="inline-flex items-center gap-2 bg-blue-50 text-blue-800 border border-blue-200 rounded-full px-4 py-2 text-sm font-medium">
              <Users className="w-4 h-4" /> {w}
            </span>
          ))}
        </div>
      </Section>

      <Section title="How it differs from a generic PDF cropper">
        <div className="flex items-start gap-4 bg-indigo-50 border border-indigo-200 rounded-2xl p-5">
          <GitCompare className="w-6 h-6 text-indigo-600 shrink-0 mt-0.5" />
          <p className="text-base text-slate-700 leading-relaxed">{cfg.comparison}</p>
        </div>
      </Section>

      <Section title="Seller tips">
        <ul className="space-y-3">
          {cfg.tips.map((t, i) => (
            <li key={i} className="flex items-start gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4">
              <Lightbulb className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
              <span className="text-sm text-slate-700">{t}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Frequently asked questions">
        <Accordion type="single" collapsible className="w-full">
          {cfg.faq.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left text-slate-900 font-semibold">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      <Section title="Related tools">
        <div className="grid sm:grid-cols-2 gap-4">
          {cfg.related.map((r) => (
            <Link key={r.path} to={r.path} data-testid={`related-${r.path}`} className="group flex items-center justify-between bg-white border border-slate-200 hover:border-blue-400 rounded-xl p-5 transition-colors">
              <span className="font-semibold text-slate-800 group-hover:text-blue-700">{r.label}</span>
              <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-transform" />
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
}
